import { StatusFilter, StatusFilterType } from "@common/types/statusFilter";

export const EMPTY_STATUS_FILTER: StatusFilter<never> = {
    filterType: StatusFilterType.ALL_SELECTED,
    kickStatuses: [],
    externalStatuses: [],
};

export function readStatusFilter<TStatus extends string>({
    statusFilter,
    kickStatuses,
    externalStatuses,
}: {
    statusFilter: StatusFilter<TStatus> | undefined;
    kickStatuses: TStatus[] | undefined;
    externalStatuses: TStatus[] | undefined;
}): StatusFilter<TStatus> | undefined {
    if (kickStatuses === undefined && externalStatuses === undefined) {
        return statusFilter;
    }
    return {
        filterType: StatusFilterType.ALL_SELECTED,
        kickStatuses: kickStatuses ?? [],
        externalStatuses: externalStatuses ?? [],
    };
}

const toStatusBucket = <TStatus extends string>(
    kickStatuses: TStatus[],
    status: TStatus,
): StatusFilter<TStatus> => ({
    filterType: StatusFilterType.ALL_SELECTED,
    kickStatuses,
    externalStatuses: [status],
});

export function narrowStatusFilterToStatus<TStatus extends string>({
    statusFilter,
    status,
    allStatuses,
}: {
    statusFilter: StatusFilter<TStatus>;
    status: TStatus;
    allStatuses: TStatus[];
}): StatusFilter<TStatus> | null {
    const { filterType, kickStatuses, externalStatuses } = statusFilter;
    const isStatusSelected = externalStatuses.includes(status);

    if (filterType === StatusFilterType.EXCLUDE_SELECTED) {
        if (isStatusSelected) {
            return null;
        }
        if (kickStatuses.length === 0) {
            return toStatusBucket([], status);
        }
        const keptKickStatuses = allStatuses.filter(
            (kickStatus) => !kickStatuses.includes(kickStatus),
        );
        return keptKickStatuses.length > 0
            ? toStatusBucket(keptKickStatuses, status)
            : null;
    }

    if (filterType === StatusFilterType.ANY_SELECTED) {
        if (
            isStatusSelected ||
            (kickStatuses.length === 0 && externalStatuses.length === 0)
        ) {
            return toStatusBucket([], status);
        }
        return kickStatuses.length > 0
            ? toStatusBucket(kickStatuses, status)
            : null;
    }

    if (externalStatuses.length > 0 && !isStatusSelected) {
        return null;
    }
    return toStatusBucket(kickStatuses, status);
}

export function isStatusFilterActive<TStatus extends string>({
    statusFilter,
    defaultExternalStatuses,
}: {
    statusFilter: StatusFilter<TStatus> | undefined;
    defaultExternalStatuses: TStatus[];
}): boolean {
    if (!statusFilter) {
        return false;
    }
    const { filterType, kickStatuses, externalStatuses } = statusFilter;
    if (kickStatuses.length > 0) {
        return true;
    }
    if (externalStatuses.length === 0) {
        return false;
    }
    const isDefaultExternalStatuses =
        externalStatuses.length === defaultExternalStatuses.length &&
        externalStatuses.every((status) =>
            defaultExternalStatuses.includes(status),
        );
    return (
        filterType === StatusFilterType.EXCLUDE_SELECTED ||
        !isDefaultExternalStatuses
    );
}
